/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { dashboardActions } from 'modules/dashboard/slice';
import { consoleCommands } from './constants';

// time given to the dashboard to settle before the vi response of a share url is loaded
export const VI_RESPONSE_DELAY = 8000;
// the pms sync toggle is enabled or disabled from the stay date, booking date and hotel that the
// commands before it set, this wait lets that state settle before the pms sync command is applied
const PMS_SYNC_DELAY = 2000;
/**
 * Pms sync command, applied after the filter commands of a tab: the toggle can become disabled
 * by those commands. Vi response and visual collapse commands follow it.
 * It has no event: the value is kept on the command run by the main command flow, so the tab of
 * the url gets it even when its filter panel is mounted after the command is executed.
 * context needs toggleDisabled.
 */
export const PMS_SYNC_STEP = {
  command: consoleCommands.PMS,
  delayBefore: PMS_SYNC_DELAY,
  apply: (value, { dispatch, toggleDisabled }) => {
    if (!toggleDisabled) {
      dispatch(dashboardActions.setPmsSync(value === 1));
    }
  },
};
/**
 * Currency command, applied after the filter commands of a tab: the hotel and compare hotel
 * commands before it set the currency of the hotel. It has no event, see PMS_SYNC_STEP.
 * A currency that is not in currencyList (the hotels do not share it) is skipped on purpose,
 * the step must not wait for it.
 * context needs currencyList.
 */
export const CURRENCY_STEP = {
  command: consoleCommands.CURRENCY,
  apply: (value, { dispatch, currencyList }) => {
    const currency = String(value).toUpperCase();
    if (currencyList?.some((item) => item?.id === currency)) {
      dispatch(dashboardActions.setLocalCurrency(currency));
    }
  },
};
/**
 * Vi response command of a share url, applied after the filter commands of a tab.
 * context needs authUser and organizationId.
 */
export const VI_RESPONSE_STEP = {
  command: consoleCommands.VI,
  ready: (context) => Boolean(context.authUser && context.organizationId),
  delayBefore: VI_RESPONSE_DELAY,
  apply: (value, { dispatch, authUser, organizationId }) => {
    dispatch(
      dashboardActions.getUserViResponseByShareId({
        organizationId,
        userId: authUser?.id,
        shareId: value,
      })
    );
  },
};
/**
 * Get the visuals of a visual collapse command value
 * @param {String} value - ex: A1,B0 or PA1,RV0
 * @returns {Array} ex: [{ name: 'A', isDisplay: true }, { name: 'B', isDisplay: false }]
 */
export const getCollapseVisuals = (value) =>
  (value?.split(',') ?? [])
    .map((visual) => /^([A-Za-z]+\d?[A-Za-z]*)(\d)$/.exec(visual.trim()))
    .filter(Boolean)
    .map(([, name, status]) => ({ name, isDisplay: status === '1' }));
/**
 * Create the visual collapse step of a tab: hide the visuals, wait, then show the ones in the command
 * @param {String} event - visual collapse event of the tab
 * @param {Number} delay - milliseconds the visuals stay hidden before the command value is applied
 * @param {Function} getActions - (context) => { [visual name]: action creator } of the active view
 * @param {Object} options - additional step fields: requires, ready, start, apply
 */
export const createVisualCollapseStep = (event, delay, getActions, options = {}) => ({
  command: consoleCommands.VISUAL_LOADING,
  event,
  requires: options.requires,
  ready: (context, run) => context.isLoaded && (options.ready?.(context, run) ?? true),
  start: (value, context, run) => {
    Object.values(getActions(context) ?? {}).forEach((action) => context.dispatch(action(false)));
    options.start?.(value, context, run);
  },
  delayBefore: delay,
  apply: (value, context, run) => {
    const actions = getActions(context) ?? {};
    getCollapseVisuals(value).forEach(({ name, isDisplay }) => {
      if (actions[name]) context.dispatch(actions[name](isDisplay));
    });
    options.apply?.(value, context, run);
    return [consoleCommands.VISUAL_LOADING, ...(options.requires ?? [])];
  },
});
